import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2, TriangleAlert, X } from 'lucide-react';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  pending?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  pending = false,
  onCancel,
  onConfirm,
}) => (
  <Dialog.Root open={open} onOpenChange={(nextOpen) => {
    if (!nextOpen && !pending) onCancel();
  }}>
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-[1000] bg-slate-950/50 backdrop-blur-sm" />
      <Dialog.Content
        className="fixed left-1/2 top-1/2 z-[1001] w-[min(28rem,calc(100%-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-slate-200 bg-white shadow-2xl focus:outline-none dark:border-slate-700 dark:bg-slate-900"
        onEscapeKeyDown={(event) => { if (pending) event.preventDefault(); }}
        onPointerDownOutside={(event) => { if (pending) event.preventDefault(); }}
      >
        <div className="flex items-start gap-4 p-6">
          <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
            destructive
              ? 'bg-red-50 text-red-600 dark:bg-red-950/50 dark:text-red-400'
              : 'bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400'
          }`}>
            <TriangleAlert className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <Dialog.Title className="text-lg font-semibold text-slate-900 dark:text-white">
              {title}
            </Dialog.Title>
            <Dialog.Description asChild>
              <div className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                {description}
              </div>
            </Dialog.Description>
          </div>
          <Dialog.Close asChild>
            <button
              type="button"
              disabled={pending}
              className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              aria-label="Close confirmation"
            >
              <X className="h-4 w-4" />
            </button>
          </Dialog.Close>
        </div>

        <div className="flex justify-end gap-3 rounded-b-2xl border-t border-slate-200 bg-slate-50/80 px-6 py-4 dark:border-slate-700 dark:bg-slate-950/40">
          <button
            type="button"
            disabled={pending}
            onClick={onCancel}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={onConfirm}
            className={`inline-flex min-w-24 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-70 ${
              destructive
                ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500/40'
                : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500/40'
            }`}
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {pending ? 'Deleting…' : confirmLabel}
          </button>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
);
